import { FC } from "react";
import { Form } from "antd";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useGetTopupCategoriesQuery } from "src/store/apis/Topup";
import { track } from "@amplitude/analytics-browser";
import useEmblaCarousel from "embla-carousel-react";
import { useDirection } from "src/i18n/direction";
import { DefaultCheckIcon } from "src/assets/Icons";

type ChipsProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const CategoryChips: FC<ChipsProps & { options: { label: string; value: string }[]; isFetching: boolean }> = ({ value, onChange, options, isFetching }) => {
  const direction = useDirection();
  const [emblaRef] = useEmblaCarousel({ loop: false, axis: "x", align: "start", dragThreshold: 6, inViewThreshold: 0.1, active: true, direction, dragFree:true });

  if (isFetching)
    return (
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-11 w-full animate-pulse rounded-md bg-background-subtle md:h-9 md:w-24 md:rounded-lg" />
        ))}
      </div>
    );

  return (
    <div ref={emblaRef} className="overflow-hidden w-full">
      <div className="flex gap-2">
        {options.map((option) => {
          const isSelected = option.value === (value ?? "");
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                track("Category Selected", { ...option });
                onChange?.(option.value);
              }}
              className={cn(
                "relative flex w-full shrink-0 cursor-pointer items-center justify-between rounded-sm p-3 text-start font-semibold whitespace-nowrap transition-colors",
                "md:w-auto md:justify-center md:rounded-full md:px-8 md:py-2 md:text-sm border",
                isSelected
                  ? "text-font-active md:bg-background-hover"
                  : "font-normal text-font-minimal hover:bg-background-hover md:hover:bg-background-hover",
              )}
            >
              {option.label}
              {isSelected && <DefaultCheckIcon className="h-4 w-4 md:hidden" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TopupCategories: FC<{ name: string }> = ({ name }) => {
  const { t, i18n } = useTranslation();
  const { data, isFetching, error } = useGetTopupCategoriesQuery({
    Page: 1,
    PageSize: 1000000,
    lang: i18n.language,
  });
  const options = [
    {
      label: t("Topup.allCategories"),
      value: "",
    },
    ...(data?.categories
      ?.filter((cat) => cat.name)
      .map((cat) => ({
        label: cat.name,
        value: cat.code,
      })) ?? []),
  ];

  if (error || (!isFetching && !data?.categories?.length)) return null;
  return (
    <Form.Item name={name} className="m-0! w-full">
      <CategoryChips options={options} isFetching={isFetching} />
    </Form.Item>
  );
};

export default TopupCategories;
